// Pointer, wheel, touch and keyboard input (spec §5.3). Routes pointer events
// to the active tool in world coordinates; handles camera gestures itself.
import type { Editor } from './editor.ts';
import type { WorldPointerEvent } from './tools/types.ts';
import { isEditableTarget } from './util/dom.ts';

const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
const MAX_WHEEL_ZOOM_DELTA = 50;
const WHEEL_ZOOM_SPEED = 0.01;

export function attachInput(ed: Editor): void {
  const target = ed.layers.interactiveCanvas;
  const touches = new Map<number, [number, number]>();
  let pinch: { mid: [number, number]; dist: number } | null = null;
  let touchLocked = false; // after a pinch, ignore leftover fingers until all lift
  let pan: { id: number; x: number; y: number } | null = null;
  let toolPointer: number | null = null;
  let spaceHeld = false;

  const local = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = target.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  const world = (e: PointerEvent): WorldPointerEvent => {
    const [sx, sy] = local(e);
    const [x, y] = ed.camera.screenToWorld(sx, sy);
    const events = e.type === 'pointermove' && e.getCoalescedEvents ? e.getCoalescedEvents() : [];
    return {
      coalesced: events.map((c) => { const [cx, cy] = local(c); return ed.camera.screenToWorld(cx, cy); }),
      x, y, sx, sy,
      button: e.button,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      modKey: isMac ? e.metaKey : e.ctrlKey,
      pointerType: e.pointerType,
      pointerId: e.pointerId,
    };
  };

  const pinchState = () => {
    const [a, b] = [...touches.values()] as [[number, number], [number, number]];
    return {
      mid: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] as [number, number],
      dist: Math.hypot(a[0] - b[0], a[1] - b[1]),
    };
  };

  target.addEventListener('pointerdown', (e) => {
    // Let keyboard shortcuts reach the board after clicking toolbar buttons.
    if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    if (e.pointerType === 'touch') {
      touches.set(e.pointerId, local(e));
      if (touches.size === 2) {
        if (toolPointer !== null) {
          ed.tool.onCancel?.();
          toolPointer = null;
        }
        pinch = pinchState();
        touchLocked = true;
        return;
      }
      if (touches.size > 2 || touchLocked) return;
    }

    target.setPointerCapture(e.pointerId);
    if (e.button === 1 || (e.button === 0 && spaceHeld)) {
      e.preventDefault();
      const [x, y] = local(e);
      pan = { id: e.pointerId, x, y };
      ed.setCursorOverride('grabbing');
      return;
    }
    if (toolPointer !== null || e.button !== 0) return;
    const w = world(e);
    // An audio clip's play button (or the playing clip's waveform) works whatever the tool (spec §19.2).
    if (!e.shiftKey && ed.audioClick(w.x, w.y)) return;
    toolPointer = e.pointerId;
    ed.tool.onPointerDown(w);
  });

  target.addEventListener('pointermove', (e) => {
    const [sx, sy] = local(e);
    ed.pointer = ed.camera.screenToWorld(sx, sy);

    if (e.pointerType === 'touch' && touches.has(e.pointerId)) {
      touches.set(e.pointerId, [sx, sy]);
      if (pinch && touches.size >= 2) {
        const next = pinchState();
        ed.camera.panBy(next.mid[0] - pinch.mid[0], next.mid[1] - pinch.mid[1]);
        if (pinch.dist > 0) ed.camera.zoomAt(next.mid[0], next.mid[1], next.dist / pinch.dist);
        pinch = next;
        return;
      }
      if (touchLocked) return;
    }

    if (pan && pan.id === e.pointerId) {
      ed.camera.panBy(sx - pan.x, sy - pan.y);
      pan.x = sx;
      pan.y = sy;
      return;
    }

    // Hover moves (no button down) go to the tool too, except for touch.
    if (toolPointer === e.pointerId || (toolPointer === null && !pan && e.pointerType !== 'touch')) {
      ed.tool.onPointerMove(world(e));
    }
  });

  const end = (e: PointerEvent, cancelled: boolean) => {
    if (e.pointerType === 'touch') {
      touches.delete(e.pointerId);
      if (touches.size < 2) pinch = null;
      if (touches.size === 0) touchLocked = false;
    }
    if (pan && pan.id === e.pointerId) {
      pan = null;
      ed.setCursorOverride(spaceHeld ? 'grab' : null);
      return;
    }
    if (toolPointer === e.pointerId) {
      toolPointer = null;
      if (cancelled) ed.tool.onCancel?.();
      else ed.tool.onPointerUp(world(e));
    }
  };
  target.addEventListener('pointerup', (e) => end(e, false));
  target.addEventListener('pointercancel', (e) => end(e, true));
  target.addEventListener('pointerleave', () => { ed.pointer = null; });
  target.addEventListener('dblclick', (e) => {
    const [sx, sy] = local(e);
    const [x, y] = ed.camera.screenToWorld(sx, sy);
    ed.handleDoubleClick(x, y, e.clientX, e.clientY);
  });
  target.addEventListener('contextmenu', (e) => e.preventDefault());

  // Wheel pans; ctrl+wheel (trackpad pinch in Chrome/Firefox) zooms around the cursor.
  // Listening on the board (not the canvas) keeps this working over the text editor.
  const board = target.parentElement!;
  board.addEventListener('wheel', (e) => {
    e.preventDefault();
    const [sx, sy] = local(e);
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? ed.layers.height : 1;
    const dx = e.deltaX * unit, dy = e.deltaY * unit;
    if (e.ctrlKey || e.metaKey) {
      const d = Math.max(-MAX_WHEEL_ZOOM_DELTA, Math.min(MAX_WHEEL_ZOOM_DELTA, dy));
      ed.camera.zoomAt(sx, sy, Math.exp(-d * WHEEL_ZOOM_SPEED));
    } else if (e.shiftKey && dx === 0) {
      ed.camera.panBy(-dy, 0);
    } else {
      ed.camera.panBy(-dx, -dy);
    }
  }, { passive: false });

  // Safari reports trackpad pinch as non-standard gesture events.
  let gestureScale = 1;
  type GestureEvent = Event & { scale: number; clientX: number; clientY: number };
  board.addEventListener('gesturestart', (e) => {
    e.preventDefault();
    gestureScale = 1;
  });
  board.addEventListener('gesturechange', (e) => {
    e.preventDefault();
    const ge = e as GestureEvent;
    const [sx, sy] = local(ge);
    ed.camera.zoomAt(sx, sy, ge.scale / gestureScale);
    gestureScale = ge.scale;
  });

  // Keys belong to a modal dialog while one is open (Esc must reach it to close it).
  const dialogOpen = () => !!document.querySelector('dialog[open]');

  window.addEventListener('keydown', (e) => {
    if (isEditableTarget(e.target) || dialogOpen()) return;
    if (e.code === 'Space') {
      e.preventDefault();
      if (!spaceHeld) {
        spaceHeld = true;
        if (!pan) ed.setCursorOverride('grab');
      }
      return;
    }
    ed.handleKeyDown(e);
  });

  window.addEventListener('keyup', (e) => {
    if (e.code !== 'Space') return;
    spaceHeld = false;
    if (!pan) ed.setCursorOverride(null);
  });

  window.addEventListener('blur', () => {
    spaceHeld = false;
    if (!pan) ed.setCursorOverride(null);
  });
}
